import React, { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, LogIn, AlertCircle } from "lucide-react";
import { useAdminAuth } from "../lib/auth";
import { formatApiErrorDetail } from "../lib/api";

export default function AdminLogin() {
  const { user, ready, login } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (!ready) return <FullPageLoader />;
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col items-center justify-center px-5">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <Link to="/" className="block text-center font-brand text-xl mb-10">
          <span className="font-bold text-brand-olive">DOGCENTER</span>{" "}
          <span className="font-semibold text-brand-sage">Lappland</span>
        </Link>

        <div className="rounded-3xl bg-white shadow-soft border border-line p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-2 text-sage">
            <Lock size={16} />
            <span className="eyebrow">Admin Login</span>
          </div>
          <h1 className="font-display text-3xl font-light text-ink leading-tight">
            Willkommen zurück.
          </h1>

          <form onSubmit={submit} className="mt-8 space-y-6" data-testid="admin-login-form">
            <div>
              <label className="eyebrow text-mutedfg block mb-1">E-Mail</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="info@dogcenter-lappland.se"
                className="input-line"
                autoComplete="email"
                required
                data-testid="admin-email"
              />
            </div>
            <div>
              <label className="eyebrow text-mutedfg block mb-1">Passwort</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-line"
                autoComplete="current-password"
                required
                data-testid="admin-password"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-sm text-rose-deep" data-testid="admin-login-error">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="admin-login-submit"
            >
              {loading ? "..." : (<><LogIn size={16} /> Anmelden</>)}
            </button>
          </form>
        </div>

        <div className="mt-6 text-center text-xs text-mutedfg">
          <Link to="/" className="link-underline">← Zurück zur Website</Link>
        </div>
      </motion.div>
    </div>
  );
}

export function FullPageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-cream text-mutedfg text-sm">
      ...
    </div>
  );
}
